import {
  FileTextOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  QuestionCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { faUserCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Dropdown, Modal, Segmented, Tooltip } from 'antd';
import React, { useState } from 'react';
import { useGetIdentity } from 'react-admin';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { setViewMode } from '../../actions/uiActions';
import { Link } from 'react-router-dom';
// @ts-ignore
import logo from '../../assets/img/netorca.png';
import Breadcrumbs from './Breadcrumbs';
import SearchBar from '../Common/SearchBar';
import Licenses from '../Licenses';
import WelcomeScreen from '../Welcome';
import useGetGeneralSettings from '../../providers/useGetGeneralSettings';
import { getActiveMembership } from '@src/components/Common/utils';
import ActivateMembership from '../Teams/SubComponents/ActivateMembership';
import TeamCreateModal from '../Teams/SubComponents/TeamCreateModal';
import NotificationInbox from './NotificationInbox';
import { ADMIN_POV, isPlatformAdmin, usePov } from '@src/components/Common/pov';

const TopBar = ({ pathName, navOpen, toggleNav, logout }: {
  pathName: string;
  /** whether the nav rail is showing — the toggle says which way it goes */
  navOpen: boolean;
  toggleNav: () => void;
  logout: any;
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const generalSettings = useGetGeneralSettings();
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(false);
  const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const feedback = generalSettings.find(setting => setting.name == 'Feedback');
  const { identity } = useGetIdentity();
  const activeMembership = getActiveMembership();
  const activeTeamName = activeMembership?.team?.name;
  const viewMode = usePov();
  const platformAdmin = isPlatformAdmin();
  /* The one point of view that is not about the reader's own team. It is a
     standing state, not something they do once, so the bar wears it: the whole
     strip is tinted, and the switch on it names which view that is. */
  const adminView = viewMode === ADMIN_POV;

  const handleViewChange = (value: string) => {
    dispatch(setViewMode(value));
    localStorage.setItem('pov_preference', value);
    history.push('/');
  };

  const fullName = [identity?.first_name, identity?.last_name].filter(Boolean).join(' ');
  const closeMenu = () => setMenuOpen(false);

  // Names here are whatever someone typed — a person's, a team's — so every one
  // of them is cut to the panel and carries the whole thing in its tooltip.
  const userMenu = (
    <div className="topbar-menu">
      <Link to="/profile/" className="topbar-menu__user" onClick={closeMenu}>
        <FontAwesomeIcon className="topbar-menu__avatar" icon={faUserCircle} />
        <span className="topbar-menu__identity">
          <span className="topbar-menu__name" title={fullName || identity?.username}>
            {fullName || identity?.username || 'Account'}
          </span>
          {fullName && identity?.username && (
            <span className="topbar-menu__username" title={identity.username}>
              {identity.username}
            </span>
          )}
        </span>
      </Link>

      <div className="topbar-menu__group">
        <span className="topbar-menu__label">Teams</span>
        <ActivateMembership onCreateTeam={() => { setIsTeamModalOpen(true); closeMenu(); }} />
      </div>

      <div className="topbar-menu__group">
        {feedback && feedback?.ENABLED && (
          <a
            className="topbar-menu__item"
            href={feedback.config?.feedback_link}
            target="_blank"
            rel="noreferrer"
            onClick={closeMenu}
          >
            <WarningOutlined className="topbar-menu__icon" />
            Report a problem
          </a>
        )}
        <button
          type="button"
          className="topbar-menu__item"
          onClick={() => { setIsLicenseModalOpen(true); closeMenu(); }}
        >
          <FileTextOutlined className="topbar-menu__icon" />
          Software licenses
        </button>
        <button
          type="button"
          className="topbar-menu__item topbar-menu__item--exit"
          onClick={() => { closeMenu(); logout(); }}
        >
          <LogoutOutlined className="topbar-menu__icon" />
          Log out
        </button>
      </div>
    </div>
  );

  return (
    <div className={`layout-v2-topbar${adminView ? ' layout-v2-topbar--admin' : ''}`}>
      <div className="layout-v2-topbar-left">
        {/* Above the rail rather than inside it: it is the way back to a nav
            that is no longer on the page, so it cannot live on the nav. */}
        <Tooltip title={navOpen ? 'Hide navigation' : 'Show navigation'} placement="bottomLeft">
          <Button
            type="text"
            className="topbar-nav-toggle"
            aria-label={navOpen ? 'Hide navigation' : 'Show navigation'}
            aria-expanded={navOpen}
            onClick={toggleNav}
            icon={navOpen ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
          />
        </Tooltip>
        <a href="/#"><img src={logo} alt="NetOrca" style={{ height: '40px' }} /></a>
        <Segmented
          className="dashboard-view-toggle"
          value={viewMode}
          onChange={(value) => handleViewChange(value as string)}
          options={[
            { label: 'Consumer', value: 'CONSUMER' },
            { label: 'Service Owner', value: 'SERVICE_OWNER' },
            /* Beside the other two rather than tucked into the account menu:
               it is the same kind of choice they are — which teams the numbers
               are about — and putting it anywhere else would make it read as a
               different kind of thing. Offered only to a platform admin, since
               it is the only point of view the API will widen.

               This segment is also what names the state while it is in force —
               see the bar's admin styling — so what it does is said here, on
               the control, rather than in a badge repeating it alongside. */
            ...(platformAdmin ? [{
              label: (
                <Tooltip title="See every team on the platform. The dashboard and team pages widen; the rest stay scoped to your own team.">
                  <span>Admin</span>
                </Tooltip>
              ),
              value: ADMIN_POV,
            }] : []),
          ]}
        />
        <Breadcrumbs path={pathName} />
      </div>
      <div className="layout-v2-topbar-right">
        <SearchBar style={{ width: 'clamp(100px, 30vw, 450px)' }} className="filter-select"/>

        <NotificationInbox />

        <Tooltip title="Getting Started">
          <QuestionCircleOutlined
            className="topbar-icon-btn"
            onClick={() => setShowWelcomeScreen(true)}
          />
        </Tooltip>

        <div className="topbar-divider" />

        <Dropdown
          dropdownRender={() => userMenu}
          trigger={['click']}
          open={menuOpen}
          onOpenChange={setMenuOpen}
        >
          <a href="#" className="topbar-user-trigger" onClick={(e) => e.preventDefault()}>
            <FontAwesomeIcon className="topbar-user-avatar" icon={faUserCircle} />
            {/* the name is cut to keep the bar's shape; the tooltip has it whole */}
            {activeTeamName && (
              <Tooltip title={activeTeamName}>
                <span className="topbar-team-name">{activeTeamName}</span>
              </Tooltip>
            )}
          </a>
        </Dropdown>
      </div>

      {/* Both modals live out here rather than inside the dropdown: interacting
          with one counts as a click outside, which closes the dropdown — and
          would take a modal rendered within it along. */}
      <Modal
        title="License Attributions"
        open={isLicenseModalOpen}
        onCancel={() => setIsLicenseModalOpen(false)}
        footer={null}
      >
        {isLicenseModalOpen && <Licenses />}
      </Modal>
      <TeamCreateModal isModalOpen={isTeamModalOpen} setIsModalOpen={setIsTeamModalOpen} />

      {showWelcomeScreen && <WelcomeScreen onClose={() => setShowWelcomeScreen(false)} />}
    </div>
  );
};

export default TopBar;
